import React, { useMemo } from "react";
import { addDays, format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

const sameDay = (a, b) => !!a && !!b && format(a, "yyyy-MM-dd") === format(b, "yyyy-MM-dd");

/**
 * Quick date-range presets (Today, Last 7 days, This month, …). Clicking one calls
 * `onSelect(from, to)` with Date objects; the active preset (matching from+to) is
 * highlighted. All "Last N days" ranges include today.
 */
export default function DateRangePresets({ from, to, onSelect, className, upcoming = false }) {
  const presets = useMemo(() => {
    const today = new Date();
    const ago = (n) => subDays(today, n);
    const prevMonth = subMonths(today, 1);
    if (upcoming) {
      const ahead = (n) => addDays(today, n);
      return [
        { key: "today", label: "Today", from: today, to: today },
        { key: "tomorrow", label: "Tomorrow", from: ahead(1), to: ahead(1) },
        { key: "next3", label: "Next 3 days", from: today, to: ahead(2) },
        { key: "next7", label: "Next 7 days", from: today, to: ahead(6) },
      ];
    }
    return [
      { key: "today", label: "Today", from: today, to: today },
      { key: "yesterday", label: "Yesterday", from: ago(1), to: ago(1) },
      { key: "last3", label: "Last 3 days", from: ago(2), to: today },
      { key: "last7", label: "Last 7 days", from: ago(6), to: today },
      { key: "last14", label: "Last 14 days", from: ago(13), to: today },
      { key: "last28", label: "Last 28 days", from: ago(27), to: today },
      { key: "last30", label: "Last 30 days", from: ago(29), to: today },
      { key: "thisMonth", label: "This month", from: startOfMonth(today), to: today },
      { key: "lastMonth", label: "Last month", from: startOfMonth(prevMonth), to: endOfMonth(prevMonth) },
    ];
  }, [upcoming]);

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {presets.map((p) => {
        const active = sameDay(from, p.from) && sameDay(to, p.to);
        return (
          <Button
            key={p.key}
            type="button"
            variant={active ? "default" : "outline"}
            size="sm"
            className="h-8 text-xs"
            onClick={() => onSelect(p.from, p.to)}
          >
            {p.label}
          </Button>
        );
      })}
    </div>
  );
}
